<script setup lang="ts">
defineProps<{
  contentClass?: string;
  sheetClass?: string;
}>();

const rootRef = useTemplateRef<HTMLElement>("rootRef");
const desktopPanelRef = useTemplateRef<HTMLElement>("desktopPanelRef");
const mobilePanelRef = useTemplateRef<HTMLElement>("mobilePanelRef");

let previousBodyOverflow = "";

const isOpen = ref(false);
const isMobile = ref(false);

function updateViewport() {
  if (typeof window === "undefined") return;

  isMobile.value = window.matchMedia("(max-width: 767px)").matches;
}

function lockBodyScroll() {
  if (typeof document === "undefined") return;
  if (!isMobile.value) return;

  previousBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";
}

function unlockBodyScroll() {
  if (typeof document === "undefined") return;

  document.body.style.overflow = previousBodyOverflow;
}

function open() {
  isOpen.value = true;
}

function close() {
  isOpen.value = false;
}

function toggle() {
  isOpen.value = !isOpen.value;
}

function handleEscPress(event: KeyboardEvent) {
  if (event.key === "Escape") {
    close();
  }
}

function handlePointerDown(event: PointerEvent) {
  if (!isOpen.value) return;

  const target = event.target as Node;

  if (rootRef.value?.contains(target)) return;
  if (desktopPanelRef.value?.contains(target)) return;
  if (mobilePanelRef.value?.contains(target)) return;

  close();
}

watch(isOpen, value => {
  if (value) {
    lockBodyScroll();
  } else {
    unlockBodyScroll();
  }
});

onMounted(() => {
  updateViewport();

  window.addEventListener("resize", updateViewport);
  window.addEventListener("keyup", handleEscPress);
  document.addEventListener("pointerdown", handlePointerDown);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateViewport);
  window.removeEventListener("keyup", handleEscPress);
  document.removeEventListener("pointerdown", handlePointerDown);
  unlockBodyScroll();
});
</script>

<template>
  <div ref="rootRef" class="relative inline-flex min-w-0 shrink-0">
    <div class="flex w-full min-w-0" @click.stop="toggle">
      <slot name="trigger" :open="isOpen" :close="close" :toggle="toggle" />
    </div>

    <!-- Desktop dropdown -->
    <Transition name="dropdown-fade">
      <div
        v-if="isOpen"
        ref="desktopPanelRef"
        class="absolute right-0 top-[calc(100%+8px)] z-[999] hidden overflow-hidden rounded-xl border border-app-border bg-white p-1.5 shadow-xl md:block"
        :class="contentClass"
      >
        <slot name="content" :open="open" :close="close" :toggle="toggle" />
      </div>
    </Transition>

    <!-- Mobile sheet -->
    <Teleport to="body">
      <Transition name="dropdown-backdrop">
        <button
          v-if="isOpen"
          type="button"
          class="fixed inset-0 z-[999] bg-black/40 md:hidden"
          aria-label="Close dropdown"
          @click="close"
        />
      </Transition>

      <Transition name="dropdown-sheet">
        <div
          v-if="isOpen"
          ref="mobilePanelRef"
          class="fixed inset-x-0 bottom-0 z-[999] flex flex-col overflow-hidden rounded-t-2xl border border-black/10 bg-white p-3 shadow-2xl md:hidden"
          :class="sheetClass"
        >
          <div
            class="mx-auto mb-3 h-1 w-10 shrink-0 rounded-full bg-[#D1D5DB]"
          />

          <slot
            name="mobile-content"
            :open="open"
            :close="close"
            :toggle="toggle"
          >
            <slot name="content" :open="open" :close="close" :toggle="toggle" />
          </slot>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition:
    opacity 140ms ease,
    transform 140ms ease;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

.dropdown-backdrop-enter-active,
.dropdown-backdrop-leave-active {
  transition: opacity 160ms ease;
}

.dropdown-backdrop-enter-from,
.dropdown-backdrop-leave-to {
  opacity: 0;
}

.dropdown-sheet-enter-active,
.dropdown-sheet-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}

.dropdown-sheet-enter-from,
.dropdown-sheet-leave-to {
  opacity: 0;
  transform: translateY(100%);
}
</style>
